import * as React from "react";
import { useTranslation } from "react-i18next";
import { useActiveFBOs } from "../../containers/ActiveFBOContainer";
import { useIdentity } from "../../containers/IdentityContainer";
import { useLocationsState } from "../../containers/LocationsStateContainer";
import { useTenants } from "../../hooks/useTenants";
import { useVipTags } from "../../hooks/useVipTags";
import { AutocompleteAircraft } from "../../screens/Settings/HangarSettings/AutocompleteAircraft";
import { Hangar, Location, Ramp, Tenant} from "../../types";
import { usePermissions } from "../../hooks/usePermissions";
import { Permission } from "../../utils/permissions";
import { ConfirmationDialog } from "../ConfirmationDialog";
import { LocationTextField } from "../LocationTextField";
import { SelectTailNumber } from "../SelectTailNumber";
import { VipTagSelect } from "../../components/VipTagSelect";

import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { formatDistance } from "date-fns";
import { addTenantConformationText } from "../../utils/phrasing";

type Props = {
  displayTypeAndLocation: boolean;
  isReference: boolean;
  tenant: Tenant;
  setTenant: (t: Tenant) => void;
  currentLocation: Location<Ramp | Hangar>;
  setCurrentLocation: (l: Location<Ramp | Hangar>) => void;
  assignedLocation: Location<Ramp | Hangar>;
  setAssignedLocation: (l: Location<Ramp | Hangar>) => void;
  onSave: () => Promise<void>;
  onClose: () => void;
};

export const EditTenantPresenter: React.FC<Props> = ({
  displayTypeAndLocation,
  tenant,
  setTenant,
  currentLocation,
  setCurrentLocation,
  assignedLocation,
  setAssignedLocation,
  onSave,
  onClose,
}) => {
  const { t } = useTranslation();
  const { activeFBO } = useActiveFBOs();
  const { airplxIdentity } = useIdentity();
  const { hasPermission } = usePermissions();
  const { locations } = useLocationsState();
  const { tenants } = useTenants();
  const { vipTags } = useVipTags(activeFBO?.id);
  const flashTimeoutRef = React.useRef<number | null>(null);
  const [shouldFlashAircraft, setShouldFlashAircraft] =
    React.useState<boolean>(false);
  const [showConfirmation, setShowConfirmation] = React.useState<{
    text: string;
    onOk: () => void;
  }>({
    text: "",
    onOk: () => {
      //
    },
  });
  const handleClose = () => {
    onClose();
  };

  const isDuplicateTailNumber = React.useMemo(() => {
    // Defensive checks - if tenant or tail_number is missing, don't block save
    if (!tenant?.id || !tenant?.tail_number) {
      return false;
    }
    return Boolean(
      tenants
        .filter((t) => t.tail_number)
        .find(
          (t) =>
            t.id !== tenant.id &&
            t.tail_number.toLocaleUpperCase() ===
              tenant.tail_number.toLocaleUpperCase()
        )
    );
  }, [tenants, tenant]);

  return (
    <Dialog
      open={true}
      onClose={handleClose}
      maxWidth="lg"
      data-element="edit_tenant"
    >
      <DialogTitle>{t('widgets.editTenant.dialogTitle')}</DialogTitle>
      <DialogContent sx={{ width: 500 }}>
        <Stack direction="column" spacing={2} sx={{ mt: 1, width: "100%" }}>
          <AutocompleteAircraft
            label={t('widgets.editTenant.selectAircraftLabel')}
            onSelect={(aircraft) => {
              if (aircraft) {
                setTenant({ ...tenant, aircraft_id: aircraft.id, aircraft });
              }
            }}
            defaultSelected={tenant.aircraft}
            shouldFlash={shouldFlashAircraft}
          />
          <Stack direction="row" spacing={2}>
            <SelectTailNumber
              disableAutocomplete={false}
              warning={isDuplicateTailNumber && t('widgets.editTenant.duplicateTailWarning')}
              tenants={tenants}
              defaultValue={tenant.tail_number}
              onInputChange={(tailNumber: string) => {
                setTenant({ ...tenant, tail_number: tailNumber });
              }}
              onChange={(selectedTenant) => {
                if (selectedTenant?.aircraft) {
                  setTenant({
                    ...tenant,
                    tail_number: selectedTenant.tail_number,
                    aircraft_id: selectedTenant.aircraft.id,
                    aircraft: selectedTenant.aircraft,
                  });

                  // Trigger flash animation when user selects from tail number dropdown
                  setShouldFlashAircraft(true);

                  // Reset flash state after the animation completes
                  if (flashTimeoutRef.current !== null) {
                    clearTimeout(flashTimeoutRef.current);
                  }
                  flashTimeoutRef.current = window.setTimeout(() => {
                    setShouldFlashAircraft(false);
                    flashTimeoutRef.current = null;
                  }, 1500);
                }
              }}
            />
            <Stack direction="column" sx={{ width: "100%" }}>
              <TextField
                inputProps={{ testid: "owner_manager" }}
                size="small"
                fullWidth
                label={t('widgets.editTenant.ownerManagerLabel')}
                onChange={(evt) =>
                  setTenant({ ...tenant, owner_manager: evt.target.value })
                }
                value={tenant.owner_manager}
              />
              <Typography variant="caption" color="error"></Typography>
            </Stack>
          </Stack>

          <Stack direction="column" spacing={1}>
            <TextField
              inputProps={{ testid: "note" }}
              size="small"
              fullWidth
              multiline
              rows={4}
              label={t('widgets.editTenant.notesLabel')}
              placeholder={t('widgets.editTenant.notesPlaceholder')}
              onChange={(evt) =>
                setTenant({
                  ...tenant,
                  note: evt.target.value,
                  note_last_updated: new Date().toISOString(),
                  note_last_updated_by: airplxIdentity?.email ?? "Unknown",
                })
              }
              value={tenant.note || ""}
            />
            {Boolean(tenant.note && tenant.note_last_updated) && (
              <Typography variant="body2" color="text.secondary">
                {t('widgets.editTenant.lastUpdate', {
                  time: formatDistance(new Date(tenant.note_last_updated), new Date(), {
                    addSuffix: true,
                  }),
                  date: new Date(tenant.note_last_updated).toLocaleDateString(),
                  user: tenant.note_last_updated_by
                })}
              </Typography>
            )}
          </Stack>

          {/* Type dropdown and Tags multi-select (separate controls) */}
          <Stack direction="row" spacing={2}>
            <FormControl size="small" sx={{ minWidth: 120 }}>
              <InputLabel>{t("widgets.editTenant.typeLabel", "Type")}</InputLabel>
              <Select
                value={tenant.type}
                label={t("widgets.editTenant.typeLabel", "Type")}
                disabled={!hasPermission(Permission.MODIFY_TENANTS)}
                onChange={(e) => {
                  const newType = e.target.value as string;
                  const wasBase = tenant.type === "base";
                  const isNowTransient = newType === "transient";

                  // If changing from base to transient, show confirmation
                  if (wasBase && isNowTransient) {
                    setShowConfirmation({
                      text: t("widgets.editTenant.transientConfirmText"),
                      onOk: () => {
                        setTenant({ ...tenant, type: newType });
                        setAssignedLocation(null);
                      },
                    });
                    return;
                  }

                  // If changing to base, show confirmation
                  if (!wasBase && newType === "base") {
                    setShowConfirmation({
                      text: addTenantConformationText(
                        currentLocation ? "hangar" : null
                      ),
                      onOk: () => setTenant({ ...tenant, type: newType }),
                    });
                    return;
                  }

                  setTenant({ ...tenant, type: newType });
                }}
              >
                <MenuItem value="transient">
                  {t("widgets.editTenant.transient", "Transient")}
                </MenuItem>
                <MenuItem value="base">
                  {t("widgets.editTenant.base", "Base")}
                </MenuItem>
              </Select>
            </FormControl>

            <VipTagSelect
              value={tenant.vip_tag_ids || []}
              availableTags={vipTags}
              disabled={!hasPermission(Permission.MODIFY_TENANTS)}
              onChange={(newTagIds) => {
                setTenant({ ...tenant, vip_tag_ids: newTagIds });
              }}
            />
          </Stack>

          {displayTypeAndLocation && (
            <Stack direction="column" spacing={2}>
              <LocationTextField
                disabled={
                  !hasPermission(Permission.MODIFY_TENANTS) ||
                  tenant.type !== "base"
                }
                isReference={true}
                unassignedText={
                  tenant.type === "base" ? t('widgets.editTenant.unassignedText') : t('widgets.editTenant.naText')
                }
                selectedLocationId={assignedLocation?.location_id}
                onChange={(newLocationId) => {
                  const newLocation = locations.find(
                    (l) => l.location_id === newLocationId
                  );
                  setAssignedLocation(newLocation);
                }}
              />
              <LocationTextField
                isReference={false}
                selectedLocationId={currentLocation?.location_id}
                unassignedText={t('widgets.editTenant.notHangaredText')}
                onChange={(newLocationId) => {
                  const newLocation = locations.find(
                    (l) => l.location_id === newLocationId
                  );
                  setCurrentLocation(newLocation);
                }}
              />
            </Stack>
          )}
        </Stack>
        {Boolean(showConfirmation?.text) && (
          <ConfirmationDialog
            title={t('widgets.editTenant.confirmTitle')}
            text={showConfirmation?.text}
            onCancel={() => {
              // do nothing
            }}
            onOk={() => showConfirmation?.onOk()}
            onClose={() => {
              setShowConfirmation({
                text: "",
                onOk: () => {
                  //
                },
              });
            }}
          />
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>{t('widgets.editTenant.cancelButton')}</Button>
        <Button
          disabled={isDuplicateTailNumber}
          variant="contained"
          color="success"
          onClick={async () => {
            await onSave();
            handleClose();
          }}
        >
          {t('widgets.editTenant.saveButton')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
